import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import Section from '../ui/Section';
import { getProductById } from '../../services/homepage.service';

const DealSection = ({ dealConfig }) => {
    const { t } = useTranslation();
    const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
    const [product, setProduct] = useState(null);

    useEffect(() => {
        if (dealConfig?.productId) {
            getProductById(dealConfig.productId).then(setProduct);
        }
    }, [dealConfig]);

    useEffect(() => {
        if (!dealConfig?.endAt) return;

        const targetDate = new Date(dealConfig.endAt).getTime();

        const interval = setInterval(() => {
            const now = new Date().getTime();
            const distance = targetDate - now;

            if (distance < 0) {
                clearInterval(interval);
                setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
            } else {
                const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((distance % (1000 * 60)) / 1000);
                setTimeLeft({ hours, minutes, seconds });
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [dealConfig]);

    if (!dealConfig?.isActive || !product) return null;

    return (
        <section className="py-12 bg-fecos-text text-white overflow-hidden relative">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent" />

            <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
                <Section className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">

                    {/* Content */}
                    <div className="space-y-6">
                        <div className="flex items-center gap-2 text-fecos-accent font-bold uppercase tracking-widest text-xs">
                            <Clock className="w-4 h-4" />
                            {t('admin.homepage.tabs.deal')}
                        </div>

                        <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
                            {product.name}
                        </h2>

                        <div className="flex gap-4">
                            {/* Timer */}
                            <div className="flex gap-4 text-center">
                                {['hours', 'minutes', 'seconds'].map((unit) => (
                                    <div key={unit} className="bg-white/10 backdrop-blur-sm rounded-lg p-3 min-w-[70px]">
                                        <div className="text-3xl font-bold font-mono">{String(timeLeft[unit]).padStart(2, '0')}</div>
                                        <div className="text-[10px] uppercase text-gray-400 mt-1">{unit}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="pt-4 flex items-center gap-6">
                            <div className="text-3xl font-bold text-fecos-accent">
                                {dealConfig.discountPercent > 0 ? (
                                    <>
                                        <span className="text-lg text-gray-500 line-through mr-3 font-normal">{product.price} ₺</span>
                                        {Math.round(product.price * (100 - dealConfig.discountPercent) / 100)} ₺
                                    </>
                                ) : (
                                    `${product.price} ₺`
                                )}
                            </div>

                            <Link
                                to={`/product/${product.id}`}
                                className="bg-white text-fecos-text px-8 py-4 rounded-full font-bold hover:bg-fecos-accent hover:text-white transition-all flex items-center gap-2"
                            >
                                {t('common.buyNow')}
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>

                    {/* Image */}
                    <div className="relative flex justify-center">
                        <div className="absolute inset-0 bg-fecos-accent/20 blur-3xl rounded-full transform scale-75" />
                        <img
                            src={product.image}
                            alt={product.name}
                            className="relative z-10 max-h-[400px] object-contain drop-shadow-2xl hover:scale-105 transition-transform duration-500"
                        />
                        {dealConfig.discountPercent > 0 && (
                            <div className="absolute top-0 right-10 bg-fecos-accent text-white w-20 h-20 rounded-full flex items-center justify-center font-bold text-xl shadow-lg z-20 animate-pulse">
                                -{dealConfig.discountPercent}%
                            </div>
                        )}
                    </div>

                </Section>
            </div>
        </section>
    );
};

export default DealSection;
